import 'whatwg-fetch';
import browserHistory from '../utils/browserHistory';
import SessionActions from '../actions/SessionActions';
import UserStore from '../stores/UserStore';
import publisher from './publisher';
import publisherPaymentDetails from './publisherPaymentDetails';
import publisherPostbacks from './publisherPostbacks';
import publisherBanners from './publisherBanners';
import publisherLandings from './publisherLandings';
import publisherContainrs from './publisherContainrs';
import PaceProgressActions from '../actions/PaceProgressActions';
import user from './user';
import runtimeConfig from '../config/runtimeConfig';
import lct from '../helpers/location';

const isDevMode = false; //process.env.NODE_ENV === 'development';
const cpaUrl = runtimeConfig.cpaUrl;
const adminUrl = runtimeConfig.adminUrl;
const clientId = runtimeConfig.clientId;

const formUrl = function (path) {
    return cpaUrl + path;
};

const formData = (data) => {
    let formData = new FormData();

    for (let key in data) {
        if (data[key]) {
            formData.append(key, data[key]);
        }
    }

    return formData;
};

export default {
    ...publisher,
    ...publisherPaymentDetails,
    ...publisherPostbacks,
    ...publisherBanners,
    ...publisherLandings,
    ...publisherContainrs,
    ...user,

    getAccessToken() {
        const location = window.location;
        const baseUrl = `${location.origin}${location.pathname}${location.search}`;
        window.location.assign(`${cpaUrl}/user/get_access_token?client_id=${clientId}&redirect_uri=${encodeURIComponent(baseUrl)}`);
    },

    updateRefreshToken(params) {
        let path = '/user/refresh_access_token';
        params.queryMethod = 'GET';
        return this.makeRequest(path, params);
    },

    makeRequest(path, params = {}) {
        SessionActions.userAuthentication();

        isDevMode ? console.info(path, 'request started') : '';

        const queryMethod = params.queryMethod || 'POST';
        const accessToken = SessionActions.getAccessData().access_token;
        let url = formUrl(path);
        let locale;

        let promise = new Promise((resolve, reject) => {
            let data = {};
            let fetchParams = {
                method: queryMethod,
                mode: 'cors',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            };

            if (params) {
                locale = params.removeLocale ? undefined : UserStore.getLocale();
                delete params.removeLocale;
                delete params.queryMethod;

                if (typeof params === 'string') {
                    data.fields = params;
                } else {
                    data = params;
                }
            }

            data.audit_referer_path = window.location.href;
            data.access_token = accessToken;
            data._locale = locale;

            if (queryMethod === 'POST') {
                fetchParams.body = formData(data);
            } else {
                url += `?${lct.stringify(params)}`;
            }

            return fetch(url, fetchParams).then(async (response) => {
                if ((response.status >= 200 && response.status < 300) || response.status === 400) {
                    return response;
                }

                let error = new Error(response.statusText);
                error.response = response;
                error.status = response.status;

                // 5xx and other errors get the same shape as the API errors ({error: {code, message}}), so the
                // usual handling (helpers/requestErrorHandler.js: notification + callback that ends the loading)
                // works for them too; 401 / 403 are left as they were (authorisation)
                if (response.status !== 401 && response.status !== 403) {
                    let message = '';
                    try {
                        const body = JSON.parse(await response.text());
                        message = (body && body.error && typeof body.error.message === 'string') ? body.error.message : '';
                    } catch (e) {
                        // not JSON (e.g. an HTML error page): a common message is shown
                    }
                    error.error = {
                        code: response.status,
                        message: message,
                        httpError: true
                    };
                }

                throw error;
            }).then(async (response) => {
                const contentType = response.headers.get('content-type') || '';
                const text = await response.text();

                if (!text || text.trim().length === 0) {
                    return {};
                }

                if (contentType.indexOf('application/json') === -1) {
                    const errorPayload = {
                        error: {
                            code: response.status || 0,
                            message: 'Unexpected response format'
                        },
                        rawBody: text,
                        status: response.status
                    };
                    if (response.status === 401 || response.status === 403 || response.redirected) {
                        SessionActions.getAccessToken();
                    }
                    return errorPayload;
                }

                try {
                    return JSON.parse(text);
                } catch (e) {
                    console.error('Failed to parse JSON:', e, text);
                    return {
                        error: {
                            code: response.status || 0,
                            message: 'Invalid JSON in response'
                        },
                        rawBody: text,
                        status: response.status
                    };
                }
            }).then((resp) => {
                if (!resp.error) {
                    isDevMode ? console.info(resp, 'success ' + path) : '';
                }
                if (resp.error) {
                    isDevMode ? console.info(resp, 'error ' + path) : '';
                    // invalid access token
                    if (resp.error.code === 401) {
                        if (SessionActions.isLoopToken()) { // Authorization cycles
                            browserHistory.push({
                                pathname: `${runtimeConfig.defaultRoute}401`,
                                state: { message: resp.error.message }
                            });
                        } else {
                            SessionActions.getAccessToken();
                        }
                    } else {
                        reject(resp);
                    }
                }

                // make asynchronous
                setTimeout(() => {
                    resolve(resp);
                }, 0);
            }).catch((resp) => {
                isDevMode ? console.info(resp, 'error ' + path) : '';
                reject(resp);
            });
        });
        PaceProgressActions.addRequest(promise);

        return promise;
    },

    makeShortUrlRequest(path, params = {}, method) {
        let url = path;
        params.ttl = parseInt(params.ttl, 10);
        if (params.id && (method === 'PUT' || method === 'DELETE')) {
            url += params.id;

            delete params.id;
        }

        const fetchParams = {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify(params),
            mode: 'cors',
        };

        let promise = new Promise((resolve, reject) => {
            fetch(url, fetchParams)
                .then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        return response;
                    } else {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                })
                .then(async (response) => {
                    const text = await response.text();
                    if (!text || text.trim().length === 0) {
                        return {};
                    }
                    try {
                        return JSON.parse(text);
                    } catch (e) {
                        console.error('Failed to parse JSON:', e, text);
                        return {};
                    }
                })
                .then((resp) => {
                    if (resp.error) {
                        if (resp.error.code === 401) {
                            if (SessionActions.isLoopToken()) {
                                browserHistory.push({
                                    pathname: `${runtimeConfig.defaultRoute}401`,
                                    state: {message: resp.error.message},
                                });
                            } else {
                                SessionActions.getAccessToken();
                            }
                        }

                        throw new Error(resp.error.message || 'Unknown error');
                    } else {
                        if (resp.short_url) {
                            resolve(resp);
                        }
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
        PaceProgressActions.addRequest(promise);

        return promise;
    }
};
